import React, { useState } from "react";
import { KModal } from "../../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../../ui-kit/k-modal/k-modal-footer";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";

interface IDemoProps {
  label: string;
  width?: number;
  centered?: boolean;
  hideHeader?: boolean;
  hideCloseIcon?: boolean;
  isLoading?: boolean;
  withFooter?: boolean;
  body?: React.ReactNode;
}

const Demo: React.FC<IDemoProps> = ({ label, withFooter, body, ...rest }: IDemoProps) => {
  const [open, setOpen] = useState<boolean>(false);
  return (
    <>
      <KuikaButton type="secondary" onClick={() => setOpen(true)}>
        {label}
      </KuikaButton>
      <KModal
        showModal={open}
        closeModal={() => setOpen(false)}
        header={<span style={{ fontWeight: 600 }}>{label}</span>}
        footer={
          withFooter ? (
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <KuikaButton type="text" onClick={() => setOpen(false)}>
                Cancel
              </KuikaButton>
              <KuikaButton type="primary" onClick={() => setOpen(false)}>
                Confirm
              </KuikaButton>
            </div>
          ) : undefined
        }
        {...rest}
      >
        <div style={{ padding: 24, color: "var(--lab-ink-muted)" }}>
          {body ?? "Default modal body content. Use the footer slot for primary actions."}
        </div>
      </KModal>
    </>
  );
};

/**
 * What a real dialog puts in that slot. `KModalFooter` is the one footer the designer's
 * dialogs use: one size, one casing, the destructive action at the far end from the commit.
 */
const StandardFooterDemo: React.FC = (): JSX.Element => {
  const [open, setOpen] = useState<boolean>(false);
  const close = (): void => setOpen(false);
  return (
    <>
      <KuikaButton type="secondary" onClick={() => setOpen(true)}>
        Open standard footer
      </KuikaButton>
      <KModal
        showModal={open}
        closeModal={close}
        header="Edit workspace"
        centered
        width={480}
        footer={
          <KModalFooter onDelete={close} deleteLabel="Delete" onCancel={close} submitLabel="Save" onSubmit={close} />
        }
      >
        <div style={{ padding: 24, color: "var(--lab-ink-muted)" }}>
          The commit sits at the right, the way out beside it, and the irreversible action at the other end of the row.
        </div>
      </KModal>
    </>
  );
};

interface IStep {
  title: string;
  lines: string[];
}

const STEPS: IStep[] = [
  { title: "Choose a user", lines: ["Pick who takes over the project."] },
  {
    title: "Confirm with a code",
    lines: [
      "We sent a six-digit code to your e-mail.",
      "Enter it to confirm the transfer.",
      "The new owner gets every permission you have today.",
      "You stay on the project as an editor."
    ]
  },
  { title: "Done", lines: ["Ownership moved."] }
];

// `stepKey` changes with the step: the old body crosses out, the new one in, and the dialog morphs
// its height between them instead of jumping.
const SteppedDemo: React.FC = (): JSX.Element => {
  const [open, setOpen] = useState<boolean>(false);
  const [stepIndex, setStepIndex] = useState<number>(0);
  const step: IStep = STEPS[stepIndex];
  const isLast: boolean = stepIndex === STEPS.length - 1;

  const close = (): void => {
    setOpen(false);
    setStepIndex(0);
  };

  return (
    <>
      <KuikaButton type="secondary" onClick={(): void => setOpen(true)}>
        Open stepped
      </KuikaButton>
      <KModal
        showModal={open}
        closeModal={close}
        header={<span style={{ fontWeight: 600 }}>{step.title}</span>}
        width={420}
        stepKey={stepIndex}
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <KuikaButton type="text" disabled={stepIndex === 0} onClick={(): void => setStepIndex(stepIndex - 1)}>
              Back
            </KuikaButton>
            <KuikaButton type="primary" onClick={isLast ? close : (): void => setStepIndex(stepIndex + 1)}>
              {isLast ? "Close" : "Next"}
            </KuikaButton>
          </div>
        }
      >
        <div style={{ padding: 24, display: "grid", gap: 8, color: "var(--lab-ink-muted)" }}>
          {step.lines.map((line: string): JSX.Element => (
            <div key={line}>{line}</div>
          ))}
        </div>
      </KModal>
    </>
  );
};

export const ModalStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="Modal"
      componentName="KModal"
      sourcePath="ui-kit/k-modal"
      description="The platform modal — an rc-dialog wrapper. Header is optional, footer is a free ReactNode slot, body content is whatever you pass as children."
    >
      <SpecRow label="Default with footer" align="start">
        <Demo label="Open default" centered width={480} withFooter />
      </SpecRow>
      <SpecRow label="Standard footer" caption="KModalFooter — what dialogs actually use" align="start">
        <StandardFooterDemo />
      </SpecRow>
      <SpecRow label="Headerless" caption="hideHeader=true" align="start">
        <Demo label="Open headerless" hideHeader width={420} withFooter />
      </SpecRow>
      <SpecRow label="No close icon" caption="hideCloseIcon=true" align="start">
        <Demo label="Open no-close" hideCloseIcon width={420} withFooter />
      </SpecRow>
      <SpecRow label="Loading body" align="start">
        <Demo label="Open loading" isLoading width={420} />
      </SpecRow>
      <SpecRow label="Wide modal" caption="width=720" align="start">
        <Demo
          label="Open wide"
          width={720}
          withFooter
          body={
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>Left column content with details and metadata.</div>
              <div>Right column content with a secondary panel.</div>
            </div>
          }
        />
      </SpecRow>
      <SpecRow label="Without footer" caption="footer omitted" align="start">
        <Demo label="Open bodyless" width={400} />
      </SpecRow>
      <SpecRow label="Stepped" caption="stepKey — height morph between steps" align="start">
        <SteppedDemo />
      </SpecRow>
    </ShowcaseFrame>
  );
};
